"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, Check, ChevronsUpDown, Filter } from "lucide-react";
import * as React from "react";

// import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/lib/components/command";
// import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/lib/components/hover-card";
import { Label } from "@/lib/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/lib/components/popover";
import { Separator } from "@/lib/components/separator";
import { Switch } from "@/lib/components/switch";
import { getProviderForModel } from "@/lib/model-utils";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import { getProviderIcon } from "@llmgateway/shared/components";
import type { ModelDefinition, ProviderDefinition, ProviderModelMapping } from "@llmgateway/models";
interface ModelSelectorProps {
  models: ModelDefinition[];
  providers: ProviderDefinition[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  rootOnly?: boolean;
  id?: string;
}
interface FilterState {
  providers: string[];
  capabilities: string[];
  priceRange: "free" | "low" | "medium" | "high" | "all";
}

// helper to extract simple capability labels from a mapping
function getMappingCapabilities(mapping?: ProviderModelMapping): string[] {
  if (!mapping) {
    return [];
  }
  const labels: string[] = [];
  if (mapping.streaming) {
    labels.push("Streaming");
  }
  if (mapping.vision) {
    labels.push("Vision");
  }
  if (mapping.tools) {
    labels.push("Tools");
  }
  if ((mapping as any).reasoning) {
    labels.push("Reasoning");
  }
  return labels;
}
export function ModelSelector(t0) {
  const $ = _c(234);
  const {
    models,
    providers,
    value,
    onValueChange,
    placeholder,
    rootOnly,
    id
  } = t0;
  const {
    t
  } = useI18n();
  const [open, setOpen] = React.useState(false);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      providers: [],
      capabilities: [],
      priceRange: "all"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [filters, setFilters] = React.useState(t1);
  const raw = value ?? "";
  let selectedProviderId;
  let selectedRegion;
  let t2;
  if ($[1] !== raw) {
    const [t3, selectedModelIdRaw] = raw.includes("/") ? raw.split("/") as [string, string] : ["", raw];
    selectedProviderId = t3;
    const lastColonIdx = selectedModelIdRaw.lastIndexOf(":");
    selectedRegion = lastColonIdx > -1 ? selectedModelIdRaw.slice(lastColonIdx + 1) : undefined;
    t2 = lastColonIdx > -1 ? selectedModelIdRaw.slice(0, lastColonIdx) : selectedModelIdRaw;
    $[1] = raw;
    $[2] = selectedProviderId;
    $[3] = selectedRegion;
    $[4] = t2;
  } else {
    selectedProviderId = $[2];
    selectedRegion = $[3];
    t2 = $[4];
  }
  const selectedModelId = t2;
  let T0;
  let T1;
  let T2;
  let availableCapabilities;
  let availableProviders;
  let capabilityLabel;
  let clearFilters;
  let filteredEntries;
  let hasActiveFilters;
  let selectedEntryKey;
  let t10;
  let t11;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let toggleCapabilityFilter;
  let toggleProviderFilter;
  let updateFilter;
  if ($[5] !== filters || $[6] !== id || $[7] !== models || $[8] !== open || $[9] !== placeholder || $[10] !== providers || $[11] !== rootOnly || $[12] !== searchQuery || $[13] !== selectedModelId || $[14] !== selectedProviderId || $[15] !== selectedRegion || $[16] !== t) {
    let t12;
    if ($[39] !== selectedModelId) {
      t12 = m => m.id === selectedModelId;
      $[39] = selectedModelId;
      $[40] = t12;
    } else {
      t12 = $[40];
    }
    const selectedModel = models.find(t12);
    let t13;
    if ($[41] !== selectedProviderId) {
      t13 = p => p.id === selectedProviderId;
      $[41] = selectedProviderId;
      $[42] = t13;
    } else {
      t13 = $[42];
    }
    const selectedProviderDef = providers.find(t13);
    const selectedMapping = selectedRegion ? selectedModel?.providers.find(p_0 => p_0.providerId === selectedProviderId && p_0.region === selectedRegion) : selectedModel?.providers.find(p_1 => p_1.providerId === selectedProviderId && !p_1.region);
    selectedEntryKey = selectedModel && selectedProviderId && selectedMapping ? `${selectedProviderId}-${selectedModel.id}-${selectedMapping.region ?? ""}` : "";
    let out;
    if ($[43] !== models || $[44] !== providers || $[45] !== rootOnly) {
      out = [];
      let t14;
      if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = new Date();
        $[47] = t14;
      } else {
        t14 = $[47];
      }
      const now = t14;
      for (const m_0 of models) {
        if (m_0.id === "custom") {
          continue;
        }
        if (rootOnly) {
          const activeProviders = m_0.providers.filter(mp => !mp.deactivatedAt || new Date(mp.deactivatedAt) > now);
          const stableProviders = activeProviders.filter(_temp);
          const candidates = stableProviders.length > 0 ? stableProviders : activeProviders;
          const cheapest = candidates.sort(_temp2)[0];
          if (cheapest) {
            out.push({
              model: m_0,
              mapping: cheapest,
              provider: providers.find(p_2 => p_2.id === cheapest.providerId)
            });
          }
        } else {
          for (const mp_1 of m_0.providers) {
            const isDeactivated = mp_1.deactivatedAt && new Date(mp_1.deactivatedAt) <= now;
            if (!isDeactivated) {
              out.push({
                model: m_0,
                mapping: mp_1,
                provider: providers.find(p_3 => p_3.id === mp_1.providerId)
              });
            }
          }
        }
      }
      $[43] = models;
      $[44] = providers;
      $[45] = rootOnly;
      $[46] = out;
    } else {
      out = $[46];
    }
    const allEntries = out;
    let t14;
    if ($[48] !== allEntries) {
      t14 = new Set(allEntries.map(_temp3));
      $[48] = allEntries;
      $[49] = t14;
    } else {
      t14 = $[49];
    }
    const ids = t14;
    let t15;
    if ($[50] !== ids || $[51] !== providers) {
      let t16;
      if ($[53] !== ids) {
        t16 = p_4 => ids.has(p_4.id as any);
        $[53] = ids;
        $[54] = t16;
      } else {
        t16 = $[54];
      }
      t15 = providers.filter(t16);
      $[50] = ids;
      $[51] = providers;
      $[52] = t15;
    } else {
      t15 = $[52];
    }
    availableProviders = t15;
    let t16;
    if ($[55] !== allEntries) {
      const set = new Set();
      allEntries.forEach(e_0 => getMappingCapabilities(e_0.mapping).forEach(c => set.add(c)));
      t16 = Array.from(set).sort();
      $[55] = allEntries;
      $[56] = t16;
    } else {
      t16 = $[56];
    }
    availableCapabilities = t16;
    let list = allEntries;
    if (searchQuery) {
      const normalize = _temp4;
      let t17;
      if ($[57] !== list || $[58] !== searchQuery) {
        const q = normalize(searchQuery);
        t17 = list.filter(t18 => {
          const {
            model,
            provider
          } = t18;
          const candidates_0 = [model.name ?? "", model.family, model.id, provider?.name ?? "", ...(model.aliases ?? [])];
          return candidates_0.some(c_0 => normalize(c_0).includes(q));
        });
        $[57] = list;
        $[58] = searchQuery;
        $[59] = t17;
      } else {
        t17 = $[59];
      }
      list = t17;
    }
    if (filters.providers.length > 0) {
      let t17;
      if ($[60] !== filters.providers || $[61] !== list) {
        t17 = list.filter(e_1 => filters.providers.includes(e_1.mapping.providerId));
        $[60] = filters.providers;
        $[61] = list;
        $[62] = t17;
      } else {
        t17 = $[62];
      }
      list = t17;
    }
    if (filters.capabilities.length > 0) {
      let t17;
      if ($[63] !== filters.capabilities || $[64] !== list) {
        t17 = list.filter(e_2 => {
          const caps = getMappingCapabilities(e_2.mapping);
          return filters.capabilities.every(c_1 => caps.includes(c_1));
        });
        $[63] = filters.capabilities;
        $[64] = list;
        $[65] = t17;
      } else {
        t17 = $[65];
      }
      list = t17;
    }
    if (filters.priceRange !== "all") {
      let t17;
      if ($[66] !== filters.priceRange || $[67] !== list) {
        t17 = list.filter(e_3 => {
          const price = Number(e_3.mapping.inputPrice ?? "0");
          switch (filters.priceRange) {
            case "free":
              {
                return price === 0;
              }
            case "low":
              {
                return price > 0 && price <= 0.000001;
              }
            case "medium":
              {
                return price > 0.000001 && price <= 0.00001;
              }
            case "high":
              {
                return price > 0.00001;
              }
            default:
              {
                return true;
              }
          }
        });
        $[66] = filters.priceRange;
        $[67] = list;
        $[68] = t17;
      } else {
        t17 = $[68];
      }
      list = t17;
    }
    filteredEntries = list;
    let t17;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = (key, value_0) => {
        setFilters(prev => ({
          ...prev,
          [key]: value_0
        }));
      };
      $[69] = t17;
    } else {
      t17 = $[69];
    }
    updateFilter = t17;
    let t18;
    if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = providerId => {
        setFilters(prev_0 => ({
          ...prev_0,
          providers: prev_0.providers.includes(providerId) ? prev_0.providers.filter(id_0 => id_0 !== providerId) : [...prev_0.providers, providerId]
        }));
      };
      $[70] = t18;
    } else {
      t18 = $[70];
    }
    toggleProviderFilter = t18;
    let t19;
    if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = capability => {
        setFilters(prev_1 => ({
          ...prev_1,
          capabilities: prev_1.capabilities.includes(capability) ? prev_1.capabilities.filter(cap => cap !== capability) : [...prev_1.capabilities, capability]
        }));
      };
      $[71] = t19;
    } else {
      t19 = $[71];
    }
    toggleCapabilityFilter = t19;
    let t20;
    if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = () => {
        setFilters({
          providers: [],
          capabilities: [],
          priceRange: "all"
        });
      };
      $[72] = t20;
    } else {
      t20 = $[72];
    }
    clearFilters = t20;
    hasActiveFilters = filters.providers.length > 0 || filters.capabilities.length > 0 || filters.priceRange !== "all";
    let t21;
    if ($[73] !== placeholder || $[74] !== t) {
      t21 = placeholder ?? t("public.tokenCalculator.form.modelPlaceholder");
      $[73] = placeholder;
      $[74] = t;
      $[75] = t21;
    } else {
      t21 = $[75];
    }
    const modelPlaceholder = t21;
    let t22;
    if ($[76] !== t) {
      t22 = capability_0 => {
        switch (capability_0) {
          case "Streaming":
            {
              return t("public.capabilities.streaming");
            }
          case "Vision":
            {
              return t("public.capabilities.vision");
            }
          case "Tools":
            {
              return t("public.capabilities.tools");
            }
          case "Reasoning":
            {
              return t("public.capabilities.reasoning");
            }
          default:
            {
              return capability_0;
            }
        }
      };
      $[76] = t;
      $[77] = t22;
    } else {
      t22 = $[77];
    }
    capabilityLabel = t22;
    T2 = Popover;
    t10 = open;
    t11 = setOpen;
    T1 = PopoverTrigger;
    t9 = true;
    T0 = Button;
    t3 = id;
    t4 = "outline";
    t5 = "combobox";
    t6 = open;
    t7 = "w-full justify-between h-12 px-4 bg-transparent";
    t8 = selectedModel ? <div className="flex items-center gap-3">{(() => {
        const iconId = rootOnly ? selectedModel.family : (selectedProviderDef ?? getProviderForModel(selectedModel, providers))?.id;
        const IconComp = iconId ? getProviderIcon(iconId as string) : null;
        const iconColor = rootOnly ? undefined : ((selectedProviderDef ?? getProviderForModel(selectedModel, providers)) as any)?.color;
        return IconComp ? <IconComp className="h-5 w-5" style={iconColor ? {
          color: iconColor
        } : undefined} /> : null;
      })()}<div className="flex flex-col items-start"><div className="flex items-center gap-1"><span className="font-medium max-w-40 truncate">{selectedModel.name}</span>{(() => {
            const mapping = selectedModel.providers.find(p_5 => p_5.providerId === selectedProviderId);
            const isDeprecated = mapping?.deprecatedAt && new Date(mapping.deprecatedAt) <= new Date();
            return isDeprecated ? <AlertTriangle className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-500" /> : null;
          })()}</div>{!rootOnly && <span className="text-xs text-muted-foreground">{(selectedProviderDef ?? getProviderForModel(selectedModel, providers))?.name}{(() => {
            const mapping_0 = selectedModel.providers.find(p_6 => p_6.providerId === selectedProviderId);
            return mapping_0?.region ? ` (${mapping_0.region})` : null;
          })()}</span>}</div></div> : modelPlaceholder;
    $[5] = filters;
    $[6] = id;
    $[7] = models;
    $[8] = open;
    $[9] = placeholder;
    $[10] = providers;
    $[11] = rootOnly;
    $[12] = searchQuery;
    $[13] = selectedModelId;
    $[14] = selectedProviderId;
    $[15] = selectedRegion;
    $[16] = t;
    $[17] = T0;
    $[18] = T1;
    $[19] = T2;
    $[20] = availableCapabilities;
    $[21] = availableProviders;
    $[22] = capabilityLabel;
    $[23] = clearFilters;
    $[24] = filteredEntries;
    $[25] = hasActiveFilters;
    $[26] = selectedEntryKey;
    $[27] = t10;
    $[28] = t11;
    $[29] = t3;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = toggleCapabilityFilter;
    $[37] = toggleProviderFilter;
    $[38] = updateFilter;
  } else {
    T0 = $[17];
    T1 = $[18];
    T2 = $[19];
    availableCapabilities = $[20];
    availableProviders = $[21];
    capabilityLabel = $[22];
    clearFilters = $[23];
    filteredEntries = $[24];
    hasActiveFilters = $[25];
    selectedEntryKey = $[26];
    t10 = $[27];
    t11 = $[28];
    t3 = $[29];
    t4 = $[30];
    t5 = $[31];
    t6 = $[32];
    t7 = $[33];
    t8 = $[34];
    t9 = $[35];
    toggleCapabilityFilter = $[36];
    toggleProviderFilter = $[37];
    updateFilter = $[38];
  }
  let t12;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
    $[78] = t12;
  } else {
    t12 = $[78];
  }
  let t13;
  if ($[79] !== T0 || $[80] !== t3 || $[81] !== t4 || $[82] !== t5 || $[83] !== t6 || $[84] !== t7 || $[85] !== t8) {
    t13 = <T0 id={t3} variant={t4} role={t5} aria-expanded={t6} className={t7}>{t8}{t12}</T0>;
    $[79] = T0;
    $[80] = t3;
    $[81] = t4;
    $[82] = t5;
    $[83] = t6;
    $[84] = t7;
    $[85] = t8;
    $[86] = t13;
  } else {
    t13 = $[86];
  }
  let t14;
  if ($[87] !== T1 || $[88] !== t13 || $[89] !== t9) {
    t14 = <T1 asChild={t9}>{t13}</T1>;
    $[87] = T1;
    $[88] = t13;
    $[89] = t9;
    $[90] = t14;
  } else {
    t14 = $[90];
  }
  let t15;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      zIndex: 99999
    };
    $[91] = t15;
  } else {
    t15 = $[91];
  }
  let t16;
  if ($[92] !== t) {
    t16 = t("public.models.selector.searchPlaceholder");
    $[92] = t;
    $[93] = t16;
  } else {
    t16 = $[93];
  }
  let t17;
  if ($[94] !== searchQuery || $[95] !== t16) {
    t17 = <CommandInput placeholder={t16} value={searchQuery} onValueChange={setSearchQuery} className="h-12 border-0" />;
    $[94] = searchQuery;
    $[95] = t16;
    $[96] = t17;
  } else {
    t17 = $[96];
  }
  const t18 = hasActiveFilters && "text-primary";
  let t19;
  if ($[97] !== t18) {
    t19 = cn("ml-2 h-8 w-8 p-0", t18);
    $[97] = t18;
    $[98] = t19;
  } else {
    t19 = $[98];
  }
  let t20;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Filter className="h-4 w-4" />;
    $[99] = t20;
  } else {
    t20 = $[99];
  }
  let t21;
  if ($[100] !== t19) {
    t21 = <PopoverTrigger asChild={true}><Button variant="ghost" size="sm" className={t19}>{t20}</Button></PopoverTrigger>;
    $[100] = t19;
    $[101] = t21;
  } else {
    t21 = $[101];
  }
  let t22;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      zIndex: 100000
    };
    $[102] = t22;
  } else {
    t22 = $[102];
  }
  let t23;
  if ($[103] !== t) {
    t23 = t("public.common.filters");
    $[103] = t;
    $[104] = t23;
  } else {
    t23 = $[104];
  }
  let t24;
  if ($[105] !== t23) {
    t24 = <h4 className="font-medium">{t23}</h4>;
    $[105] = t23;
    $[106] = t24;
  } else {
    t24 = $[106];
  }
  let t25;
  if ($[107] !== clearFilters || $[108] !== hasActiveFilters || $[109] !== t) {
    t25 = hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters}>{t("public.common.clearAll")}</Button>;
    $[107] = clearFilters;
    $[108] = hasActiveFilters;
    $[109] = t;
    $[110] = t25;
  } else {
    t25 = $[110];
  }
  let t26;
  if ($[111] !== t24 || $[112] !== t25) {
    t26 = <div className="flex items-center justify-between">{t24}{t25}</div>;
    $[111] = t24;
    $[112] = t25;
    $[113] = t26;
  } else {
    t26 = $[113];
  }
  let t27;
  if ($[114] !== t) {
    t27 = t("public.common.providers");
    $[114] = t;
    $[115] = t27;
  } else {
    t27 = $[115];
  }
  let t28;
  if ($[116] !== t27) {
    t28 = <Label className="text-sm font-medium">{t27}</Label>;
    $[116] = t27;
    $[117] = t28;
  } else {
    t28 = $[117];
  }
  let t29;
  if ($[118] !== availableProviders || $[119] !== filters.providers || $[120] !== toggleProviderFilter) {
    let t30;
    if ($[122] !== filters.providers || $[123] !== toggleProviderFilter) {
      t30 = (provider_0, index) => {
        const ProviderIcon = getProviderIcon(provider_0.id);
        return <div key={`${provider_0.id}-${index}`} className="flex items-center space-x-2"><Switch id={`provider-${provider_0.id}`} checked={filters.providers.includes(provider_0.id)} onCheckedChange={() => toggleProviderFilter(provider_0.id)} /><Label htmlFor={`provider-${provider_0.id}`} className="flex items-center gap-2 text-sm cursor-pointer">{ProviderIcon && <ProviderIcon className="h-3 w-3" style={{
              color: provider_0.color
            }} />}{provider_0.name}</Label></div>;
      };
      $[122] = filters.providers;
      $[123] = toggleProviderFilter;
      $[124] = t30;
    } else {
      t30 = $[124];
    }
    t29 = availableProviders.map(t30);
    $[118] = availableProviders;
    $[119] = filters.providers;
    $[120] = toggleProviderFilter;
    $[121] = t29;
  } else {
    t29 = $[121];
  }
  let t30;
  if ($[125] !== t29) {
    t30 = <div className="space-y-2 max-h-32 overflow-y-auto">{t29}</div>;
    $[125] = t29;
    $[126] = t30;
  } else {
    t30 = $[126];
  }
  let t31;
  if ($[127] !== t28 || $[128] !== t30) {
    t31 = <div className="space-y-2">{t28}{t30}</div>;
    $[127] = t28;
    $[128] = t30;
    $[129] = t31;
  } else {
    t31 = $[129];
  }
  let t32;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Separator />;
    $[130] = t32;
  } else {
    t32 = $[130];
  }
  let t33;
  if ($[131] !== t) {
    t33 = t("public.common.capabilities");
    $[131] = t;
    $[132] = t33;
  } else {
    t33 = $[132];
  }
  let t34;
  if ($[133] !== t33) {
    t34 = <Label className="text-sm font-medium">{t33}</Label>;
    $[133] = t33;
    $[134] = t34;
  } else {
    t34 = $[134];
  }
  let t35;
  if ($[135] !== availableCapabilities || $[136] !== capabilityLabel || $[137] !== filters.capabilities || $[138] !== toggleCapabilityFilter) {
    let t36;
    if ($[140] !== capabilityLabel || $[141] !== filters.capabilities || $[142] !== toggleCapabilityFilter) {
      t36 = capability_1 => <div key={capability_1} className="flex items-center space-x-2"><Switch id={`capability-${capability_1}`} checked={filters.capabilities.includes(capability_1)} onCheckedChange={() => toggleCapabilityFilter(capability_1)} /><Label htmlFor={`capability-${capability_1}`} className="text-sm cursor-pointer">{capabilityLabel(capability_1)}</Label></div>;
      $[140] = capabilityLabel;
      $[141] = filters.capabilities;
      $[142] = toggleCapabilityFilter;
      $[143] = t36;
    } else {
      t36 = $[143];
    }
    t35 = availableCapabilities.map(t36);
    $[135] = availableCapabilities;
    $[136] = capabilityLabel;
    $[137] = filters.capabilities;
    $[138] = toggleCapabilityFilter;
    $[139] = t35;
  } else {
    t35 = $[139];
  }
  let t36;
  if ($[144] !== t35) {
    t36 = <div className="space-y-2 max-h-32 overflow-y-auto">{t35}</div>;
    $[144] = t35;
    $[145] = t36;
  } else {
    t36 = $[145];
  }
  let t37;
  if ($[146] !== t34 || $[147] !== t36) {
    t37 = <div className="space-y-2">{t34}{t36}</div>;
    $[146] = t34;
    $[147] = t36;
    $[148] = t37;
  } else {
    t37 = $[148];
  }
  let t38;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <Separator />;
    $[149] = t38;
  } else {
    t38 = $[149];
  }
  let t39;
  if ($[150] !== t) {
    t39 = t("public.models.selector.priceRange");
    $[150] = t;
    $[151] = t39;
  } else {
    t39 = $[151];
  }
  let t40;
  if ($[152] !== t39) {
    t40 = <Label className="text-sm font-medium">{t39}</Label>;
    $[152] = t39;
    $[153] = t40;
  } else {
    t40 = $[153];
  }
  let t41;
  if ($[154] !== t) {
    t41 = t("public.models.selector.allModels");
    $[154] = t;
    $[155] = t41;
  } else {
    t41 = $[155];
  }
  let t42;
  if ($[156] !== t41) {
    t42 = {
      value: "all",
      label: t41
    };
    $[156] = t41;
    $[157] = t42;
  } else {
    t42 = $[157];
  }
  let t43;
  if ($[158] !== t) {
    t43 = t("public.models.selector.freeModels");
    $[158] = t;
    $[159] = t43;
  } else {
    t43 = $[159];
  }
  let t44;
  if ($[160] !== t43) {
    t44 = {
      value: "free",
      label: t43
    };
    $[160] = t43;
    $[161] = t44;
  } else {
    t44 = $[161];
  }
  let t45;
  if ($[162] !== t) {
    t45 = t("public.models.selector.lowCost");
    $[162] = t;
    $[163] = t45;
  } else {
    t45 = $[163];
  }
  let t46;
  if ($[164] !== t45) {
    t46 = {
      value: "low",
      label: t45
    };
    $[164] = t45;
    $[165] = t46;
  } else {
    t46 = $[165];
  }
  let t47;
  if ($[166] !== t) {
    t47 = t("public.models.selector.mediumCost");
    $[166] = t;
    $[167] = t47;
  } else {
    t47 = $[167];
  }
  let t48;
  if ($[168] !== t47) {
    t48 = {
      value: "medium",
      label: t47
    };
    $[168] = t47;
    $[169] = t48;
  } else {
    t48 = $[169];
  }
  let t49;
  if ($[170] !== t) {
    t49 = t("public.models.selector.highCost");
    $[170] = t;
    $[171] = t49;
  } else {
    t49 = $[171];
  }
  let t50;
  if ($[172] !== t49) {
    t50 = {
      value: "high",
      label: t49
    };
    $[172] = t49;
    $[173] = t50;
  } else {
    t50 = $[173];
  }
  let t51;
  if ($[174] !== t42 || $[175] !== t44 || $[176] !== t46 || $[177] !== t48 || $[178] !== t50) {
    t51 = [t42, t44, t46, t48, t50];
    $[174] = t42;
    $[175] = t44;
    $[176] = t46;
    $[177] = t48;
    $[178] = t50;
    $[179] = t51;
  } else {
    t51 = $[179];
  }
  let t52;
  if ($[180] !== filters.priceRange || $[181] !== t51 || $[182] !== updateFilter) {
    t52 = <div className="space-y-2">{t51.map(option => <div key={option.value} className="flex items-center space-x-2"><Switch id={`price-${option.value}`} checked={filters.priceRange === option.value} onCheckedChange={() => updateFilter("priceRange", option.value)} /><Label htmlFor={`price-${option.value}`} className="text-sm cursor-pointer">{option.label}</Label></div>)}</div>;
    $[180] = filters.priceRange;
    $[181] = t51;
    $[182] = updateFilter;
    $[183] = t52;
  } else {
    t52 = $[183];
  }
  let t53;
  if ($[184] !== t40 || $[185] !== t52) {
    t53 = <div className="space-y-2">{t40}{t52}</div>;
    $[184] = t40;
    $[185] = t52;
    $[186] = t53;
  } else {
    t53 = $[186];
  }
  let t54;
  if ($[187] !== t26 || $[188] !== t31 || $[189] !== t37 || $[190] !== t53) {
    t54 = <PopoverContent className="w-80" style={t22} side="right" align="start"><div className="space-y-4">{t26}{t31}{t32}{t37}{t38}{t53}</div></PopoverContent>;
    $[187] = t26;
    $[188] = t31;
    $[189] = t37;
    $[190] = t53;
    $[191] = t54;
  } else {
    t54 = $[191];
  }
  let t55;
  if ($[192] !== filterOpen || $[193] !== t21 || $[194] !== t54) {
    t55 = <Popover open={filterOpen} onOpenChange={setFilterOpen}>{t21}{t54}</Popover>;
    $[192] = filterOpen;
    $[193] = t21;
    $[194] = t54;
    $[195] = t55;
  } else {
    t55 = $[195];
  }
  let t56;
  if ($[196] !== t17 || $[197] !== t55) {
    t56 = <div className="flex items-center border-b px-3 w-full">{t17}{t55}</div>;
    $[196] = t17;
    $[197] = t55;
    $[198] = t56;
  } else {
    t56 = $[198];
  }
  let t57;
  if ($[199] !== t) {
    t57 = t("public.models.selector.noModels");
    $[199] = t;
    $[200] = t57;
  } else {
    t57 = $[200];
  }
  let t58;
  if ($[201] !== clearFilters || $[202] !== hasActiveFilters || $[203] !== t) {
    t58 = hasActiveFilters && <Button variant="link" size="sm" onClick={clearFilters} className="mt-2">{t("public.models.selector.clearFilters")}</Button>;
    $[201] = clearFilters;
    $[202] = hasActiveFilters;
    $[203] = t;
    $[204] = t58;
  } else {
    t58 = $[204];
  }
  let t59;
  if ($[205] !== t57 || $[206] !== t58) {
    t59 = <CommandEmpty>{t57}{t58}</CommandEmpty>;
    $[205] = t57;
    $[206] = t58;
    $[207] = t59;
  } else {
    t59 = $[207];
  }
  const t60 = filteredEntries.length === 1 ? "public.models.selector.modelFound" : "public.models.selector.modelsFound";
  let t61;
  if ($[208] !== filteredEntries.length || $[209] !== t || $[210] !== t60) {
    t61 = t(t60, {
      count: filteredEntries.length
    });
    $[208] = filteredEntries.length;
    $[209] = t;
    $[210] = t60;
    $[211] = t61;
  } else {
    t61 = $[211];
  }
  let t62;
  if ($[212] !== t61) {
    t62 = <div className="px-2 py-1 text-xs text-muted-foreground">{t61}</div>;
    $[212] = t61;
    $[213] = t62;
  } else {
    t62 = $[213];
  }
  let t63;
  if ($[214] !== filteredEntries || $[215] !== onValueChange || $[216] !== rootOnly || $[217] !== selectedEntryKey) {
    t63 = filteredEntries.map(t64 => {
      const {
        model: model_0,
        mapping: mapping_1,
        provider: provider_1
      } = t64;
      const IconComp_0 = rootOnly ? getProviderIcon(model_0.family) : provider_1 ? getProviderIcon(provider_1.id) : null;
      const entryKey = `${mapping_1.providerId}-${model_0.id}-${mapping_1.region ?? ""}`;
      const isDeprecated_0 = mapping_1.deprecatedAt && new Date(mapping_1.deprecatedAt) <= new Date();
      return <CommandItem key={entryKey} value={entryKey} onSelect={() => {
        onValueChange?.(`${mapping_1.providerId}/${model_0.id}${mapping_1.region ? `:${mapping_1.region}` : ""}`);
        setOpen(false);
      }} className="p-3 cursor-pointer"><Check className={cn("h-4 w-4", entryKey === selectedEntryKey ? "opacity-100" : "opacity-0")} /><div className="flex items-center justify-between w-full"><div className="flex items-center gap-2">{IconComp_0 ? <IconComp_0 className="h-6 w-6 flex-shrink-0" /> : null}<div className="flex flex-col"><div className="flex items-center gap-1"><span className="font-medium">{model_0.name}</span>{isDeprecated_0 && <AlertTriangle className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-500" />}</div>{!rootOnly && <span className="text-xs text-muted-foreground">{provider_1?.name}{mapping_1.region && <span className="ml-1">({mapping_1.region})</span>}</span>}</div></div></div></CommandItem>;
    });
    $[214] = filteredEntries;
    $[215] = onValueChange;
    $[216] = rootOnly;
    $[217] = selectedEntryKey;
    $[218] = t63;
  } else {
    t63 = $[218];
  }
  let t64;
  if ($[219] !== t62 || $[220] !== t63) {
    t64 = <CommandGroup>{t62}{t63}</CommandGroup>;
    $[219] = t62;
    $[220] = t63;
    $[221] = t64;
  } else {
    t64 = $[221];
  }
  let t65;
  if ($[222] !== t59 || $[223] !== t64) {
    t65 = <CommandList className="max-h-[400px]">{t59}{t64}</CommandList>;
    $[222] = t59;
    $[223] = t64;
    $[224] = t65;
  } else {
    t65 = $[224];
  }
  let t66;
  if ($[225] !== t56 || $[226] !== t65) {
    t66 = <PopoverContent className="w-[600px] p-0" style={t15} sideOffset={4}><div className="flex"><div className="flex-1"><Command shouldFilter={false}>{t56}{t65}</Command></div></div></PopoverContent>;
    $[225] = t56;
    $[226] = t65;
    $[227] = t66;
  } else {
    t66 = $[227];
  }
  let t67;
  if ($[228] !== T2 || $[229] !== t10 || $[230] !== t11 || $[231] !== t14 || $[232] !== t66) {
    t67 = <T2 open={t10} onOpenChange={t11}>{t14}{t66}</T2>;
    $[228] = T2;
    $[229] = t10;
    $[230] = t11;
    $[231] = t14;
    $[232] = t66;
    $[233] = t67;
  } else {
    t67 = $[233];
  }
  return t67;
}
function _temp4(s) {
  return s.toLowerCase().replace(/[-_\s]+/g, "");
}
function _temp3(e) {
  return e.mapping.providerId;
}
function _temp2(a, b) {
  return Number(a.inputPrice ?? "0") - Number(b.inputPrice ?? "0");
}
function _temp(mp_0) {
  return !mp_0.stability || mp_0.stability === "stable" || mp_0.stability === "beta";
}